import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { complaintReqApi, FN01 } from '../../../services/complaintReqApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useComplaintReqForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FN01[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [code, setCode] = useState('');
  const [descCN, setDescCN] = useState('');
  const [descEN, setDescEN] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await complaintReqApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setCode(current.fn01_02);
      setDescCN(current.fn01_11);
      setDescEN(current.fn01_12 || '');
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setCode('');
    setDescCN('');
    setDescEN('');
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setCode(current.fn01_02);
    setDescCN(current.fn01_11);
    setDescEN(current.fn01_12 || '');
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      setCode(current.fn01_02);
      setDescCN(current.fn01_11);
      setDescEN(current.fn01_12 || '');
    }
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await complaintReqApi.create({ fn01_02: code, fn01_11: descCN, fn01_12: descEN });
        message.success('新增成功');
      } else {
        await complaintReqApi.update(current!.fn01_02, { fn01_11: descCN, fn01_12: descEN });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetCode = code;
      const idx = data.findIndex(r => r.fn01_02 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await complaintReqApi.delete(current.fn01_02);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.fn01_02 >= searchText);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode,
    descCN, setDescCN,
    descEN, setDescEN,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    loadRecords,
  };
}
